import { toast } from "@gigpro/ui/Toast";
import { ApiError } from "@sidegigllc/api";
import {
  getLocationsStatsRetrieveQueryKey,
  getNextPageParam,
  getStafferGigsV2CalendarListQueryKey,
  getStafferGigsV2ListInfiniteQueryKey,
  getStafferGigsV2ListQueryKey,
  getStafferGigsV2RecurringPatternGigsListQueryKey,
  gigCostsV2BusinessRetrieve,
  stafferGigsV2PartialUpdate,
  stafferGigsV2RetrieveWithTransform,
  testingMoveGigToPastCreate,
  getStafferGigsV2ListInfiniteWithTransformQueryOptions,
  useStafferGigsV2ListWithTransform,
  type GigCertificationRequirementWriteRequest,
  type GigCostsV2BusinessRetrieveParams,
  type PaginatedStafferGigViewReadonlySerializerV2List,
  type StafferGigsV2ListParams,
} from "@sidegigllc/api/client";
import {
  type InfiniteData,
  keepPreviousData,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { stafferGigV2DetailToStafferGigDetail, stafferGigV2ToStafferGig } from "api/resources/gigs";
import type { GigSettingKeys, StafferGig, StafferGigDetail } from "api/resources/StafferGig";
import { getActiveLocationId } from "services/atoms";
import type { MutationHook } from "./types";

const GIGS_BASE_KEY = "STAFFER_GIGS";
const GIG_MOVE_TO_PAST = `${GIGS_BASE_KEY}_MOVE_TO_PAST`;

export type GigSettingsPayload = { [key in GigSettingKeys]: boolean };

export const useShiftCostsValidation: MutationHook<
  Awaited<ReturnType<typeof gigCostsV2BusinessRetrieve>>,
  GigCostsV2BusinessRetrieveParams
> = (options) =>
  useMutation({
    mutationFn: (payload) => gigCostsV2BusinessRetrieve(payload),
    ...options,
  });

const FETCH_GIG = "FETCH_GIG";

export const gigQueryOptions = (gigId: number | null | undefined) =>
  queryOptions({
    queryKey: [FETCH_GIG, gigId],
    queryFn:
      gigId == null
        ? skipToken
        : () => stafferGigsV2RetrieveWithTransform(gigId).then(stafferGigV2DetailToStafferGigDetail),
  });

export const useGig = (
  { id }: { id: number | null | undefined },
  queryOptions: Omit<
    UseQueryOptions<StafferGigDetail, Error, StafferGigDetail, Array<string | number | null | undefined>>,
    "queryKey" | "queryFn"
  > = {},
) => useQuery({ ...gigQueryOptions(id), ...queryOptions });

export const useRefetchGig = (staticGigId?: number) => {
  const queryClient = useQueryClient();
  return async (gigId: number | undefined = staticGigId) => {
    const locationId = getActiveLocationId();
    const statsKey = locationId ? getLocationsStatsRetrieveQueryKey(locationId) : null;
    // Only a location still before its first post can change the answer that decides what Ask Qwick opens.
    const beforeFirstPost =
      !!statsKey &&
      queryClient.getQueryData<{ has_posted_first_gig?: boolean }>(statsKey)?.has_posted_first_gig === false;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: gigId ? [FETCH_GIG, gigId] : [FETCH_GIG] }),
      queryClient.invalidateQueries({ queryKey: getStafferGigsV2CalendarListQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getStafferGigsV2RecurringPatternGigsListQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getStafferGigsV2ListQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getStafferGigsV2ListInfiniteQueryKey() }),
      // Cached for the session otherwise.
      ...(beforeFirstPost && statsKey ? [queryClient.invalidateQueries({ queryKey: statsKey })] : []),
    ]);
  };
};

export type GigsQueryPayload = StafferGigsV2ListParams & {
  has_event?: boolean;
};

type GigListResult = Omit<PaginatedStafferGigViewReadonlySerializerV2List, "results"> & {
  results: Array<StafferGig>;
};

type GigsQueryOptions = Omit<
  UseQueryOptions<PaginatedStafferGigViewReadonlySerializerV2List, void, GigListResult>,
  "queryKey" | "queryFn" | "select"
>;

export const selectStafferGigs = (data: PaginatedStafferGigViewReadonlySerializerV2List): GigListResult => ({
  ...data,
  results: data.results.map(stafferGigV2ToStafferGig),
});

export const useGigs = (params: StafferGigsV2ListParams, options?: GigsQueryOptions) =>
  useStafferGigsV2ListWithTransform<GigListResult>(params, {
    query: { ...options, select: selectStafferGigs },
  });

export const queryListGigsOptions = (payload: StafferGigsV2ListParams, options?: { enabled?: boolean }) =>
  getStafferGigsV2ListInfiniteWithTransformQueryOptions<InfiniteData<GigListResult, StafferGigsV2ListParams["page"]>>(
    payload,
    {
      query: {
        getNextPageParam,
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60,
        ...options,
        select: (data) => ({
          ...data,
          pages: data.pages.map((page) => ({ ...page, results: page.results.map(stafferGigV2ToStafferGig) })),
        }),
      },
    },
  );

export const useQueryListGigs = (payload: StafferGigsV2ListParams, options?: { enabled?: boolean }) =>
  useInfiniteQuery(queryListGigsOptions(payload, options));

export const useMoveGigToPast = () => {
  const refetch = useRefetchGig();
  return useMutation({
    mutationKey: [GIG_MOVE_TO_PAST],
    mutationFn: (id: StafferGig["id"]) => testingMoveGigToPastCreate(id),
    onSuccess: () => refetch(),
  });
};

export const useUpdateGigSettings = (id: StafferGig["id"]) => {
  const refetch = useRefetchGig(id);
  return useMutation({
    mutationKey: ["settings"],
    mutationFn: (payload: Partial<GigSettingsPayload>) => stafferGigsV2PartialUpdate(id, payload),
    onSuccess: () => refetch(),
    onError: (error) => {
      const response = ApiError.fromThrown(error);
      toast.error(response.getMainErrorMessage() ?? "Failed to update shift settings");
    },
  });
};

// Push Back start time override for a single shift. `null` clears the override
// (the shift falls back to the location default).
export const useUpdateGigPushBack = (id: StafferGig["id"]) => {
  const refetch = useRefetchGig(id);
  return useMutation({
    mutationKey: ["push-back", id],
    mutationFn: (push_back_minutes: number | null) => stafferGigsV2PartialUpdate(id, { push_back_minutes }),
    onSuccess: () => refetch(),
    onError: (error) => {
      const response = ApiError.fromThrown(error);
      toast.error(response.getMainErrorMessage() ?? "Failed to update push back start time");
    },
  });
};

export type UpdateGigCertificationRequirementsPayload = {
  certification_requirements: Array<GigCertificationRequirementWriteRequest>;
};

export const useUpdateGigCertificationRequirements = (id: StafferGig["id"]) => {
  const refetch = useRefetchGig(id);
  return useMutation({
    mutationKey: ["gig-certification-requirements", id],
    mutationFn: (payload: UpdateGigCertificationRequirementsPayload) => stafferGigsV2PartialUpdate(id, payload),
    onSuccess: () => refetch(),
  });
};
